import FormulaParser from "fast-formula-parser";
import type { SheetData, SheetRow } from "../models";
import type { FormulaCell } from "../cells";

/**
 * Formula Engine wrapper that understands our generic SheetData structure.
 * It uses fast-formula-parser under the hood for evaluating isolated mathematical equations.
 */
export class FormulaEngine {
    private parser: any; // fast-formula-parser doesn't export the type as a namespace construct cleanly

    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        this.parser = new (FormulaParser as any)();
    }

    /**
     * Evaluates all formula cells in a given sheet and returns the newly computed rows
     */
    public evaluateSheet(sheet: SheetData): SheetRow[] {
        const newRows = sheet.rows.map((row) => {
            const newCells = row.cells.map((cell) => {
                if (cell.type === "formula") {
                    const parsedValue = this.evaluateFormula(
                        (cell as FormulaCell).formula,
                    );
                    return { ...cell, value: parsedValue };
                }
                return cell;
            });

            // Optimization: Only return a new object if cells actually changed (shallow compare)
            const hasChanges = newCells.some((c, i) => c !== row.cells[i]);
            return hasChanges ? { ...row, cells: newCells } : row;
        });

        return newRows;
    }

    /**
     * Attempts to parse a single formula strictly mathematically.
     */
    private evaluateFormula(formula: string): string | number {
        try {
            // formula typically looks like "=5*2", parser wants "5*2"
            const exp = formula.startsWith("=") ? formula.slice(1) : formula;
            const res = this.parser.parse(exp);

            // The parser could return errors like "#DIV/0!"
            if (typeof res === "object" && res !== null && "error" in res) {
                return res.error; // e.g. "#VALUE!"
            }
            return res as number | string;
        } catch (err) {
            console.error("Formula parsing error for:", formula, err);
            return "#ERROR!";
        }
    }
}
